import React from 'react';
import { withRouter } from 'react-router-dom';
import { Button, HyperLink } from '@ok/okd';

import { toLocale } from '@common/i18n';

import history from '../../common/utils/history';

import DetailHeader from './components/DetailHeader';
import { BLOCK_MODIFIER, CONTENT_BLOCK_TYPE } from './consts';
import {
  usePositionDetail,
  useRecruitPositionId,
  useScrollRoll,
} from './hooks';

import './job.less';

// Trimmed related-position description; '' when missing (hides the summary block)
const getRelatedSummary = (related = {}) => (related.description || '').trim();

const ApplyButton = ({ job, loading, className, category }) => (
  <Button
    className={className}
    category={category}
    disabled={loading || !job.id}
    onClick={() => history.push(`/recruit/submit/${job.id}`)}
  >
    {toLocale('recruit_apply')}
  </Button>
);

// Render content blocks; unknown types fall back to the paragraph style
const SectionContent = ({ blocks = [] }) => (
  <ul className="recruit-job__content-list">
    {blocks.map((block, idx) => {
      const modifier =
        BLOCK_MODIFIER[block.type] ||
        BLOCK_MODIFIER[CONTENT_BLOCK_TYPE.PARAGRAPH];
      return (
        <li
          key={block.content || idx}
          className={`recruit-job__content-block recruit-job__content-block--${modifier}`}
        >
          {block.content}
        </li>
      );
    })}
  </ul>
);

const Job = () => {
  // isRoll drives the compact sticky header (full-size on mobile)
  const isRoll = useScrollRoll({ disableOnMobile: true });
  const positionId = useRecruitPositionId();
  const { detail: job, isLoading } = usePositionDetail(positionId);
  const relatedJobs = job.relatedPositions || [];

  return (
    <div className="container">
      <div className="recruit-job">
        <DetailHeader
          block="recruit-job"
          isRoll={isRoll}
          name={job.name}
          tags={job.tags}
          breadcrumb={[
            toLocale('recruit_job_openings'),
            toLocale('recruit_job_description'),
          ]}
          action={
            <ApplyButton
              job={job}
              loading={isLoading}
              className="recruit-job__title-button"
            />
          }
          rollAction={
            <ApplyButton
              job={job}
              loading={isLoading}
              className="recruit-job__title-button--roll"
            />
          }
        />
        <div className="recruit-job__body">
          <div className="recruit-job__content">
            {job?.sections?.map((section, idx) => (
              <React.Fragment key={section.title || idx}>
                <div className="recruit-job__content-title">
                  {section.title}
                </div>
                <SectionContent blocks={section.contentBlocks} />
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="recruit-job__related">
          <div className="recruit-job__related-inner">
            <div className="recruit-job__related-body">
              <div className="recruit-job__related-header">
                <div className="recruit-job__related-title">
                  {toLocale('recruit_job_relevant_title')}
                </div>
                <HyperLink
                  className="recruit-job__related-more"
                  withRightIcon
                  onClick={() => {
                    history.push('/recruit');
                  }}
                >
                  {toLocale('recruit_job_more')}
                </HyperLink>
              </div>
              <div className="recruit-job__related-grid">
                {relatedJobs.map((relatedJob) => {
                  const summary = getRelatedSummary(relatedJob);
                  return (
                    <div
                      key={relatedJob.id}
                      className="recruit-job__related-card"
                      onClick={() => {
                        history.push(`/recruit/job/${relatedJob.id}`);
                      }}
                    >
                      <span className="recruit-job__related-department">
                        {relatedJob.departmentName}
                      </span>
                      <div className="recruit-job__related-card-title">
                        {relatedJob.name}
                      </div>
                      {summary && (
                        <div className="recruit-job__related-summary">
                          {summary}
                        </div>
                      )}
                      <div className="recruit-job__related-tags">
                        {relatedJob.tags?.map((tag, tagIdx) => (
                          <div
                            key={tagIdx}
                            className="recruit-job__related-tag"
                          >
                            {tag}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="recruit-job__mobile-bar">
          <Button
            className="recruit-job__mobile-back"
            category={Button.CATEGORY.outline}
            onClick={() => {
              history.push('/recruit');
            }}
          >
            {toLocale('recruit_back')}
          </Button>
          <ApplyButton
            job={job}
            loading={isLoading}
            className="recruit-job__mobile-apply"
            category={Button.CATEGORY.fill}
          />
        </div>
      </div>
    </div>
  );
};

export default withRouter(Job);
